import React, { useEffect, useState, useRef } from 'react';
import { PersonaVetada } from '../../types/personaVetada';
import { getPersonaVetadaByCedula } from '../../services/General/personaVetadaService';
import PersonaSearch from '../shared/PersonaSearchPicker';
import { Persona } from '../../types/persona';

interface PersonaVetadaModalProps {
    personaVetada: PersonaVetada;
    onClose: () => void;
    onSave: (personaVetada: PersonaVetada) => void;
}

const PersonaVetadaModal: React.FC<PersonaVetadaModalProps> = ({ personaVetada, onClose, onSave }) => {
    const [cedula, setCedula] = useState<string>('');
    const [nombre, setNombre] = useState<string>('');
    const [motivo, setMotivo] = useState<string>('');
    const [fechaDesde, setFechaDesde] = useState<string>('');
    const [observaciones, setObservaciones] = useState<string>('');
    const [isActive, setIsActive] = useState<boolean>(true);
    const [isFormValid, setIsFormValid] = useState<boolean>(false);
    const [errors, setErrors] = useState<{ cedula?: string; nombre?: string; motivo?: string; fechaDesde?: string }>({});
    const [personaData, setPersonaData] = useState<Persona | null>(null);
    const motivoInputRef = useRef<HTMLTextAreaElement>(null);

    const handlePersonaSelect = (persona: Persona) => {
        setCedula(persona.CEDULA);
        setNombre(persona.NOMBRE);
        setPersonaData(persona);
        // Si la persona ya tiene un nombre, validamos inmediatamente
        if (persona.CEDULA) {
            validateField('cedula', persona.CEDULA);
        }
        if (persona.NOMBRE) {
            validateField('nombre', persona.NOMBRE);
        }
    };

    useEffect(() => {
        if (personaVetada.veto_id) {
            setCedula(personaVetada.cedula);
            setNombre(personaVetada.nombre);
            setMotivo(personaVetada.motivo);
            setFechaDesde(personaVetada.fecha_desde.split('T')[0]); // Formato YYYY-MM-DD
            setObservaciones(personaVetada.observaciones || '');
            setIsActive(personaVetada.is_active);
            setErrors({});
            //Si estamos editando, enfocar el motivo
            if (motivoInputRef.current) {
                motivoInputRef.current.focus();
            }
        } else {
            // Si estamos creando, establecer fecha actual por defecto
            setFechaDesde(new Date().toISOString().split('T')[0]); // Fecha actual en formato YYYY-MM-DD
        }
    }, [personaVetada]);

    useEffect(() => {
        const validarErrores =
            cedula.trim() !== '' &&
            nombre.trim() !== '' &&
            motivo.trim() !== '' &&
            fechaDesde.trim() !== '' &&
            (!errors.cedula || errors.cedula === '') &&
            (!errors.nombre || errors.nombre === '') &&
            (!errors.motivo || errors.motivo === '') &&
            (!errors.fechaDesde || errors.fechaDesde === '');
        setIsFormValid(validarErrores);
    }, [cedula, nombre, motivo, fechaDesde, errors]);

    const handleSave = () => {
        onSave({
            ...personaVetada,
            cedula,
            nombre,
            motivo,
            fecha_desde: fechaDesde,
            observaciones,
            is_active: isActive
        });
    };

    const validateCedula = async (cedula: string) => {
        // Si estamos editando y la cédula no cambió, no validamos duplicados
        if (personaVetada.veto_id && cedula === personaVetada.cedula) {
            return { isValid: true, errorMessage: '' };
        }
        try {
            const existingPersona: PersonaVetada | null = await getPersonaVetadaByCedula(cedula);

            console.log('Resultado de búsqueda por cédula:', existingPersona);
            console.log('Veto: ', existingPersona?.veto_id);
            // Si no se encontró la persona, es válido (puede crear)
            if (!existingPersona || existingPersona.veto_id === undefined) {
                return { isValid: true, errorMessage: '' };
            }

            // Si se encontró y no es el mismo registro que estamos editando
            if (existingPersona.veto_id && existingPersona.veto_id !== personaVetada.veto_id) {
                return {
                    isValid: false,
                    errorMessage: `La cédula ${existingPersona.cedula} ya está registrada a nombre de ${existingPersona.nombre}`
                };
            }

            return { isValid: true, errorMessage: '' };
        } catch (error: any) {
            // Si el error es 404 (no encontrado) está bien, significa que la cédula no existe
            if (error.response && error.response.status !== 404) {
                return {
                    isValid: false,
                    errorMessage: error.message || 'Error validando la cédula'
                };
            }
            return { isValid: true, errorMessage: '' };
        }
    };

    const validateField = async (fieldName: string, value: string) => {
        let errorMessage = '';
        if (value.trim() === '') {
            errorMessage = 'Este campo es requerido';
            setErrors((prevErrors) => ({ ...prevErrors, [fieldName]: errorMessage }));
            return;
        }

        // Para cédula, validar longitud mínima y formato
        if (fieldName === 'cedula') {
            if (value.trim().length < 10) {
                errorMessage = 'La cédula debe tener al menos 10 caracteres';
                setErrors((prevErrors) => ({ ...prevErrors, [fieldName]: errorMessage }));
                return;
            }

            const validation = await validateCedula(value);
            if (!validation?.isValid) {
                setErrors((prevErrors) => ({ ...prevErrors, [fieldName]: validation?.errorMessage }));
            } else {
                setErrors((prevErrors) => ({ ...prevErrors, [fieldName]: '' }));
            }
        } else {
            // Para otros campos, solo validar que no estén vacíos
            setErrors((prevErrors) => ({ ...prevErrors, [fieldName]: '' }));
        }
    };


    return (
        <div className="modal show d-block" tabIndex={-1} role="dialog">
            <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                <div className="modal-content shadow-lg bg-light">
                    <div className="modal-header bg-primary text-white shadow-sm py-2">
                        <h5 className="modal-title fs-6 fw-semibold">
                            {personaVetada.veto_id ? 'Editar' : 'Crear'} Persona Vetada ({personaVetada.veto_id || 'Nueva'})
                        </h5>
                        <button type="button" className="btn-close btn-close-white" aria-label="Close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        {/* Campo de búsqueda de persona - SOLO en modo creación */}
                        <div className="container-fluid">
                            <div className="row mb-3">
                                <div className="col-12">
                                    {!personaVetada.veto_id ? (
                                        <div className="form-group mb-2">
                                            <label htmlFor="personaSearch" className='form-label'>Buscar Persona por Cédula *</label>
                                            <PersonaSearch
                                                onPersonaSelect={handlePersonaSelect}
                                                value={cedula}
                                            />
                                            {errors.cedula && <div className="text-danger small mt-1">{errors.cedula}</div>}
                                            <small className="form-text text-muted">
                                                Escribe la cédula para buscar y seleccionar una persona. Se autocompletará el nombre.
                                            </small>
                                        </div>
                                    ) : (
                                        // En modo edición, mostrar cédula como campo de solo lectura
                                        <div className="form-group mb-2">
                                            <label htmlFor="cedula" className="forma-label">Cédula</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="cedula"
                                                value={cedula}
                                                readOnly
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="row mb-3">
                                <div className="col-12">
                                    {/* Campo de nombre - en modo edición es solo lectura, en creación se autocompleta */}
                                    <div className="form-group mb-2">
                                        <label htmlFor="nombre" className='form-label'>Nombre *</label>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.nombre ? 'is-invalid' : ''}`}
                                            id="nombre"
                                            value={nombre}
                                            disabled
                                            readOnly={!personaVetada.veto_id && !!personaData} // Solo lectura si se seleccionó de búsqueda
                                            onChange={(e) => {
                                                setNombre(e.target.value);
                                                validateField('nombre', e.target.value);
                                            }}
                                            onBlur={e => validateField('nombre', e.target.value)}
                                        />
                                        <div className="invalid-feedback">{errors.nombre}</div>
                                    </div>
                                </div>
                            </div>
                            <div className="row mb-3">
                                <div className="col-12">
                                    <div className="form-group mb-2">
                                        <label htmlFor="motivo">Motivo *</label>
                                        <textarea
                                            className={`form-control ${errors.motivo ? 'is-invalid' : ''}`}
                                            id="motivo"
                                            value={motivo}
                                            ref={motivoInputRef}
                                            onBlur={e => validateField('motivo', e.target.value)}
                                            onChange={(e) => setMotivo(e.target.value)}
                                            rows={2}
                                            placeholder="Describe el motivo por el cual se veta a esta persona..."
                                        />
                                        <div className="invalid-feedback">{errors.motivo}</div>
                                    </div>
                                </div>
                            </div>
                            {/* Fila 4: Dos columnas - Fecha Desde y Estado */}
                            <div className="row mb-3">
                                <div className="col-md-6 mb-3 mb-md-0">
                                    <div className="form-group mb-2">
                                        <label htmlFor="fechaDesde" className='form-label'>Fecha Desde *</label>
                                        <input
                                            type="date"
                                            className={`form-control ${errors.fechaDesde ? 'is-invalid' : ''}`}
                                            id="fechaDesde"
                                            value={fechaDesde}
                                            onBlur={e => validateField('fechaDesde', e.target.value)}
                                            onChange={(e) => setFechaDesde(e.target.value)}
                                        />
                                        <div className="invalid-feedback">{errors.fechaDesde}</div>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="form-group">
                                        <label className="form-label d-block">&nbsp;</label> {/* Etiqueta invisible para alineación */}
                                        <div className="form-check mt-md-4">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                id="isActive"
                                                checked={isActive}
                                                onChange={(e) => setIsActive(e.target.checked)}
                                            />
                                            <label className="form-check-label" htmlFor="isActive">
                                                Persona Actualmente vetada
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="row mb-3">
                                <div className="col-12">
                                    <div className="form-group mb-2">
                                        <label htmlFor="observaciones" className='form-label'>Observaciones</label>
                                        <textarea
                                            className="form-control"
                                            id="observaciones"
                                            value={observaciones}
                                            onChange={(e) => setObservaciones(e.target.value)}
                                            rows={2}
                                            placeholder="Información adicional o detalles sobre el veto..."
                                        />
                                    </div>

                                </div>
                            </div>
                            <div className="row mb-2">
                                <div className="col-12">
                                    <div className="alert alert-info">
                                        <small>
                                            <i className="bi bi-info-circle me-2"></i>
                                            <strong>Nota:</strong> Una persona vetada no podrá acceder a las instalaciones mientras el veto esté activo.
                                        </small>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer py-2 bg-light border-top">
                            <button type="button" className="btn btn-sm btn-secondary" onClick={onClose}>
                                Cancelar
                            </button>
                            <button type="button" className="btn btn-sm btn-primary" onClick={handleSave} disabled={!isFormValid}>
                                {personaVetada.veto_id ? 'Actualizar' : 'Guardar'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PersonaVetadaModal;